import { Data, Effect } from 'effect';
import { useEffect, useMemo, useRef, useState } from 'react';
import type { CSSProperties, ReactElement } from 'react';
import type {
  Bbox,
  Block,
  PdfReadyInfo,
  PdfRegionSelection,
  PdfSurfaceProps,
  Point,
  ViewportLike,
} from './types';
import { OverlayBox } from './OverlayBox';
import { loadPdf, type LoadedPage, type LoadedPdf, type LoadOptions } from './lib/pdfjs';

export class PdfSurfaceError extends Data.TaggedError('PdfSurfaceError')<{
  readonly operation: 'load' | 'measure' | 'render';
  readonly message: string;
}> {}

const pdfSurfaceErrorCauses = new WeakMap<PdfSurfaceError, unknown>();

function pdfSurfaceFailure(
  operation: PdfSurfaceError['operation'],
  cause: unknown,
): PdfSurfaceError {
  const error = new PdfSurfaceError({
    operation,
    message: 'Unable to render the PDF surface.',
  });
  pdfSurfaceErrorCauses.set(error, cause);
  return error;
}

export function pdfSurfaceErrorCause(error: PdfSurfaceError): unknown {
  return pdfSurfaceErrorCauses.get(error) ?? error;
}

export function restorePdfSurfaceError<A>(
  program: Effect.Effect<A, PdfSurfaceError>,
): Effect.Effect<A, unknown> {
  return program.pipe(Effect.catch((error) => Effect.fail(pdfSurfaceErrorCause(error))));
}

export function runPdfSurfacePromise<A>(program: Effect.Effect<A, PdfSurfaceError>): Promise<A> {
  return Effect.runPromise(restorePdfSurfaceError(program));
}

export const loadPdfSurfaceDocumentEffect = Effect.fn('pdfSurface.loadDocument')(function* (
  source: PdfSurfaceProps['pdf'],
  options: LoadOptions = {},
): Effect.fn.Return<LoadedPdf, PdfSurfaceError> {
  return yield* Effect.tryPromise({
    try: () => loadPdf(source, options),
    catch: (cause) => pdfSurfaceFailure('load', cause),
  });
});

export const measurePdfSurfacePageEffect = Effect.fn('pdfSurface.measurePage')(function* (
  document: LoadedPdf,
  page: number,
): Effect.fn.Return<LoadedPage, PdfSurfaceError> {
  return yield* Effect.tryPromise({
    try: () => document.getPage(page),
    catch: (cause) => pdfSurfaceFailure('measure', cause),
  });
});

export const renderPdfSurfacePageEffect = Effect.fn('pdfSurface.renderPage')(function* (
  page: LoadedPage,
  context: CanvasRenderingContext2D,
  signal?: AbortSignal,
): Effect.fn.Return<void, PdfSurfaceError> {
  return yield* Effect.tryPromise({
    try: () => page.render(context, signal),
    catch: (cause) => pdfSurfaceFailure('render', cause),
  });
});

/**
 * `<PdfSurface>` — the single React primitive for rendering a PDF with bbox
 * overlays. Two layout modes via the `mode` prop:
 *
 *   mode="page"        — render one page (`page` prop, 1-indexed).
 *   mode="continuous"  — render all pages stacked, lazy via IntersectionObserver.
 *
 * Never sets a fixed pixel height on the page-stage element; always uses
 * `aspect-ratio` so the overlay layer tracks the rendered canvas exactly
 * when the container width is constrained. See the regression test in
 * tests/PdfSurface.dom.test.tsx for the 2026-05-11 aspect-ratio bug.
 */
export function PdfSurface(props: PdfSurfaceProps) {
  const {
    pdf,
    mode = 'page',
    page = 1,
    scale = 1.5,
    overlays,
    activeKey,
    // Default is "click" — the canonical case across all migration consumers
    // (parse-validator, pdf-browser, playground viewer). Callers that want
    // truly display-only bboxes opt in with interaction="readonly".
    interaction = 'click',
    onRegionClick,
    onRegionHover,
    onRegionSelect,
    onReady,
    onVisiblePageChange,
  } = props;

  const [loaded, setLoaded] = useState<LoadedPdf | null>(null);
  // Sparse: index = pageNum - 1. Slots are filled as pages are measured.
  const [pages, setPages] = useState<(LoadedPage | null)[]>([]);
  const [err, setErr] = useState<Error | null>(null);

  // 1. Load the PDF when source changes.
  useEffect(() => {
    let cancelled = false;
    setErr(null);
    setLoaded(null);
    setPages([]);
    (async () => {
      try {
        const doc = await runPdfSurfacePromise(loadPdfSurfaceDocumentEffect(pdf, { scale }));
        if (cancelled) return;

        // Codex P2 (round 3): measure the visible page FIRST so first paint
        // doesn't wait for every page. A 712-page LandCruiser PDF would
        // otherwise spend ~hundreds of getPage() awaits before showing
        // anything. For mode="page" we measure just `page`; for
        // mode="continuous" we measure page 1 then stream the rest in.
        const initialPage = mode === 'page' ? Math.max(1, Math.min(doc.numPages, page)) : 1;
        const first = await runPdfSurfacePromise(measurePdfSurfacePageEffect(doc, initialPage));
        if (cancelled) return;
        const initialPages: (LoadedPage | null)[] = new Array(doc.numPages).fill(null);
        initialPages[initialPage - 1] = first;
        setLoaded(doc);
        setPages(initialPages.slice()); // shallow copy to satisfy React identity

        const aspectRatios: Record<number, number> = {
          [initialPage]: first.viewport.width / first.viewport.height,
        };
        onReady?.({ numPages: doc.numPages, aspectRatios } satisfies PdfReadyInfo);

        // Codex P2 (round 5): only stream the remaining pages in continuous
        // mode. mode="page" relies on the page-change effect below for
        // on-demand loading; pre-measuring every page burned CPU/memory on
        // large docs (the canonical 712-page LandCruiser case) for pages
        // that would never be shown.
        if (mode !== 'continuous') return;
        for (let p = 1; p <= doc.numPages; p++) {
          if (p === initialPage) continue;
          const lp = await runPdfSurfacePromise(measurePdfSurfacePageEffect(doc, p));
          if (cancelled) return;
          initialPages[p - 1] = lp;
          // Only update React state every ~16 pages or on the last page to
          // avoid a render storm. Replace with a fresh array so referential
          // identity changes for the children diff.
          if (p === doc.numPages || p % 16 === 0) {
            setPages(initialPages.slice());
          }
        }
      } catch (e) {
        if (cancelled) return;
        setErr(e instanceof Error ? e : new Error(String(e)));
      }
    })();
    return () => {
      cancelled = true;
    };
    // pdf is a structural source; consumer should pass a stable reference. We
    // intentionally don't deep-compare here — pass new object only to reload.
    // mode is included so switching page↔continuous rebuilds the load
    // strategy (page-mode skips the streaming loop; continuous needs it).
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pdfKey(pdf), scale, mode]);

  // Codex P2 (round 4): when the caller changes `page` in mode="page", the
  // initial-load effect above does NOT rerun (its deps exclude `page`), so a
  // jump from p1 → p500 would render p1 until the background sequential loop
  // got there. Prioritize the requested page directly.
  useEffect(() => {
    if (!loaded || mode !== 'page') return;
    const idx = page - 1;
    if (idx < 0 || idx >= pages.length) return;
    if (pages[idx]) return; // already measured
    let cancelled = false;
    (async () => {
      try {
        const lp = await runPdfSurfacePromise(measurePdfSurfacePageEffect(loaded, page));
        if (cancelled) return;
        setPages((cur) => {
          if (cur[idx]) return cur; // raced with background loop
          const next = cur.slice();
          next[idx] = lp;
          return next;
        });
      } catch {
        // swallow — error UI is parent-owned
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [loaded, mode, page, pages]);

  // Hooks must run unconditionally — keep useMemo above any early-return.
  const overlaysByPage = useMemo(() => groupOverlaysByPage(overlays ?? []), [overlays]);

  if (err) {
    return (
      <div role="alert" data-pdf-surface-error="true" style={{ padding: 16, color: '#c83232' }}>
        Couldn't load PDF: {err.message}
      </div>
    );
  }
  // Loading: nothing loaded yet, OR no page measured yet (sparse-array path).
  if (!loaded || !pages.some(Boolean)) {
    return <div data-pdf-surface-loading="true" />;
  }

  if (mode === 'continuous') {
    return (
      <ContinuousView
        pages={pages}
        overlaysByPage={overlaysByPage}
        interaction={interaction}
        activeKey={activeKey}
        onRegionClick={onRegionClick}
        onRegionHover={onRegionHover}
        onRegionSelect={onRegionSelect}
        onVisiblePageChange={onVisiblePageChange}
      />
    );
  }

  // Codex P2 (round 7): mode="page" must NOT fall back to another measured
  // page when the requested one isn't ready. The previous fallback showed
  // page 1's canvas + overlays while the surrounding UI said "page N",
  // routing clicks to the wrong regions. Show a loading state instead.
  const requestedIdx = Math.max(0, Math.min(pages.length - 1, page - 1));
  const target = pages[requestedIdx];
  if (!target) return <div data-pdf-surface-loading="true" data-pdf-pending-page={page} />;
  return (
    <div data-pdf-surface="page">
      <PageStage
        loadedPage={target}
        overlays={overlaysByPage.get(target.pageNumber) ?? []}
        interaction={interaction}
        activeKey={activeKey}
        onRegionClick={onRegionClick}
        onRegionHover={onRegionHover}
        onRegionSelect={onRegionSelect}
      />
    </div>
  );
}

// ─── ContinuousView ────────────────────────────────────────────────────

interface ContinuousViewProps {
  pages: (LoadedPage | null)[];
  overlaysByPage: Map<number, Block[]>;
  interaction: NonNullable<PdfSurfaceProps['interaction']>;
  activeKey?: string | null;
  onRegionClick?(block: Block): void;
  onRegionHover?(block: Block | null): void;
  onRegionSelect?(selection: PdfRegionSelection): void;
  onVisiblePageChange?(page: number): void;
}

function ContinuousView({
  pages,
  overlaysByPage,
  interaction,
  activeKey,
  onRegionClick,
  onRegionHover,
  onRegionSelect,
  onVisiblePageChange,
}: ContinuousViewProps): ReactElement {
  const containerRef = useRef<HTMLDivElement | null>(null);

  // Codex P2 (round 2): wire onVisiblePageChange. Observe each page-stage;
  // the page whose center is closest to the container's viewport center
  // becomes the "current" page. Cheaper than full scroll listeners, debounced
  // by IntersectionObserver's own batching.
  useEffect(() => {
    if (!onVisiblePageChange || !containerRef.current) return;
    const container = containerRef.current;
    const visibility = new Map<number, number>(); // page → intersection ratio
    let lastReported = -1;
    const observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          const p = Number((entry.target as HTMLElement).dataset.page);
          if (!Number.isFinite(p)) continue;
          visibility.set(p, entry.intersectionRatio);
        }
        let best = -1;
        let bestRatio = 0;
        for (const [p, ratio] of visibility) {
          if (ratio > bestRatio) {
            bestRatio = ratio;
            best = p;
          }
        }
        if (best > 0 && best !== lastReported) {
          lastReported = best;
          onVisiblePageChange(best);
        }
      },
      { root: container, threshold: [0, 0.25, 0.5, 0.75, 1] },
    );
    const stages = container.querySelectorAll<HTMLElement>('[data-page]');
    stages.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
    // Codex P2 (round 4): rebind whenever the `pages` array changes (i.e.
    // placeholders get replaced with real PageStages). Watching pages.length
    // alone was stale because length stays constant at doc.numPages — the
    // observer kept references to detached placeholder divs.
  }, [pages, onVisiblePageChange]);

  return (
    <div
      ref={containerRef}
      data-pdf-surface="continuous"
      style={{ overflowY: 'auto', height: '100%' }}
    >
      {pages.map((lp, idx) =>
        lp ? (
          <PageStage
            key={lp.pageNumber}
            loadedPage={lp}
            overlays={overlaysByPage.get(lp.pageNumber) ?? []}
            interaction={interaction}
            activeKey={activeKey}
            onRegionClick={onRegionClick}
            onRegionHover={onRegionHover}
            onRegionSelect={onRegionSelect}
          />
        ) : (
          // Skeleton placeholder for unmeasured pages — preserves scroll height.
          <div
            key={`pending-${idx}`}
            data-page={idx + 1}
            style={{ height: 200, margin: '0 auto 12px', background: 'rgba(0,0,0,0.04)' }}
          />
        ),
      )}
    </div>
  );
}

// ─── PageStage ─────────────────────────────────────────────────────────

interface PageStageProps {
  loadedPage: LoadedPage;
  overlays: Block[];
  interaction: NonNullable<PdfSurfaceProps['interaction']>;
  activeKey?: string | null;
  onRegionClick?(block: Block): void;
  onRegionHover?(block: Block | null): void;
  onRegionSelect?(selection: PdfRegionSelection): void;
}

function PageStage({
  loadedPage,
  overlays,
  interaction,
  activeKey,
  onRegionClick,
  onRegionHover,
  onRegionSelect,
}: PageStageProps): ReactElement {
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const stageRef = useRef<HTMLDivElement | null>(null);
  const { viewport, pageNumber } = loadedPage;
  const [drawStart, setDrawStart] = useState<Point | null>(null);
  const [drawBox, setDrawBox] = useState<Bbox | null>(null);
  // Codex P1 (round 6): gate the canvas render on viewport visibility. Even
  // though continuous mode only mounts non-null PageStages, every mount was
  // immediately decoding its canvas — opening a 712-page PDF rasterized all
  // 712 pages. With this gate, off-screen pages mount but don't render until
  // they're within ~400px of the viewport.
  const [shouldRender, setShouldRender] = useState(false);

  useEffect(() => {
    if (typeof IntersectionObserver === 'undefined' || !stageRef.current) {
      // No IO support (SSR / old test envs) — render eagerly.
      setShouldRender(true);
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            setShouldRender(true);
            io.disconnect();
            break;
          }
        }
      },
      { rootMargin: '400px' },
    );
    io.observe(stageRef.current);
    return () => io.disconnect();
  }, []);

  useEffect(() => {
    if (!shouldRender) return;
    // Codex P2 (round 7): use AbortController so quick page navigation
    // cancels the in-flight render before starting a new one. Without
    // this, two render tasks can paint into the same canvas.
    const controller = new AbortController();
    (async () => {
      if (!canvasRef.current) return;
      canvasRef.current.width = viewport.width;
      canvasRef.current.height = viewport.height;
      const ctx = canvasRef.current.getContext('2d');
      if (!ctx) return;
      try {
        await runPdfSurfacePromise(renderPdfSurfacePageEffect(loadedPage, ctx, controller.signal));
      } catch (e) {
        if (controller.signal.aborted) return;
        if (e instanceof DOMException && e.name === 'AbortError') return;
        // swallow other errors — error UI is owned by the parent component
      }
    })();
    return () => {
      controller.abort();
    };
  }, [loadedPage, shouldRender]);

  const stageStyle: CSSProperties = {
    position: 'relative',
    width: viewport.width,
    maxWidth: '100%',
    // REGRESSION GUARD (2026-05-11): use aspect-ratio, NEVER a fixed height.
    aspectRatio: `${viewport.width} / ${viewport.height}`,
    boxShadow: '0 2px 8px rgba(0,0,0,0.10)',
    background: 'white',
    margin: '0 auto 12px',
    cursor: interaction === 'draw' ? 'crosshair' : undefined,
    touchAction: interaction === 'draw' ? 'none' : undefined,
  };

  const interactionMode = interaction === 'click' ? 'click' : 'readonly';
  const drawEnabled = interaction === 'draw';

  return (
    <div
      ref={stageRef}
      data-testid={`page-stage-${pageNumber}`}
      data-page={pageNumber}
      style={stageStyle}
      onPointerDown={(event) => {
        if (!drawEnabled || (typeof event.button === 'number' && event.button !== 0)) return;
        const point = pointFromPointerEvent(event.currentTarget, event.clientX, event.clientY);
        setDrawStart(point);
        setDrawBox({ ...point, w: 0.01, h: 0.01 });
        event.currentTarget.setPointerCapture?.(event.pointerId);
        event.preventDefault();
      }}
      onPointerMove={(event) => {
        if (!drawEnabled || !drawStart) return;
        setDrawBox(
          rectFromPoints(
            drawStart,
            pointFromPointerEvent(event.currentTarget, event.clientX, event.clientY),
          ),
        );
        event.preventDefault();
      }}
      onPointerUp={(event) => {
        if (!drawEnabled || !drawStart) return;
        const point = pointFromPointerEvent(event.currentTarget, event.clientX, event.clientY);
        const bbox = minimumSelectionRect(rectFromPoints(drawStart, point));
        setDrawStart(null);
        setDrawBox(bbox);
        event.currentTarget.releasePointerCapture?.(event.pointerId);
        onRegionSelect?.({
          page: pageNumber,
          bbox,
          point,
          selectedAt: new Date().toISOString(),
          source: 'draw',
        });
        event.preventDefault();
      }}
      onPointerCancel={(event) => {
        if (!drawEnabled) return;
        setDrawStart(null);
        event.currentTarget.releasePointerCapture?.(event.pointerId);
      }}
    >
      <canvas ref={canvasRef} style={{ display: 'block', width: '100%', height: 'auto' }} />
      {overlays.map((block) => (
        <OverlayBox
          key={`${block.page}:${block.idx}`}
          block={block}
          focused={activeKey === `${block.page}:${block.idx}`}
          mode={interactionMode}
          onClick={onRegionClick}
          onHover={onRegionHover}
        />
      ))}
      {drawBox ? <DrawSelectionBox bbox={drawBox} /> : null}
    </div>
  );
}

// ─── helpers ───────────────────────────────────────────────────────────

function groupOverlaysByPage(blocks: Block[]): Map<number, Block[]> {
  const m = new Map<number, Block[]>();
  for (const b of blocks) {
    const list = m.get(b.page);
    if (list) list.push(b);
    else m.set(b.page, [b]);
  }
  return m;
}

function DrawSelectionBox({ bbox }: { bbox: Bbox }): ReactElement {
  return (
    <div
      aria-hidden="true"
      data-pdf-draw-selection="true"
      style={{
        position: 'absolute',
        left: `${bbox.x * 100}%`,
        top: `${bbox.y * 100}%`,
        width: `${bbox.w * 100}%`,
        height: `${bbox.h * 100}%`,
        border: '2px solid var(--citation-selection, var(--okra-selection, #b45122))',
        background: 'rgba(180, 81, 34, 0.14)',
        boxSizing: 'border-box',
        pointerEvents: 'none',
      }}
    />
  );
}

function pointFromPointerEvent(element: HTMLElement, clientX: number, clientY: number): Point {
  const rect = element.getBoundingClientRect();
  const width = rect.width || 1;
  const height = rect.height || 1;
  return {
    x: clip01((clientX - rect.left) / width),
    y: clip01((clientY - rect.top) / height),
  };
}

function rectFromPoints(a: Point, b: Point): Bbox {
  const x = Math.min(a.x, b.x);
  const y = Math.min(a.y, b.y);
  return { x, y, w: Math.abs(a.x - b.x), h: Math.abs(a.y - b.y) };
}

function minimumSelectionRect(bbox: Bbox): Bbox {
  if (bbox.w >= 0.003 || bbox.h >= 0.003) return bbox;
  return {
    x: Math.min(0.98, bbox.x),
    y: Math.min(0.98, bbox.y),
    w: 0.02,
    h: 0.02,
  };
}

function clip01(value: number): number {
  return Math.max(0, Math.min(1, value));
}

// Codex P2: byte-backed sources are keyed by object identity, not byteLength.
// Two different PDFs of the same byte length must reload the effect; same
// buffer reference must NOT. A WeakMap of source-object → monotonic ID gives
// us a stable primitive React can depend on without retaining the bytes.
const bytesIdMap = new WeakMap<object, number>();
let bytesIdCounter = 0;
function bytesId(bytes: ArrayBuffer | Uint8Array): number {
  const key = bytes as unknown as object;
  let id = bytesIdMap.get(key);
  if (id === undefined) {
    id = ++bytesIdCounter;
    bytesIdMap.set(key, id);
  }
  return id;
}

/** Build a stable string key for a PdfSource so React effects can depend on it. */
function pdfKey(source: PdfSurfaceProps['pdf']): string {
  if ('url' in source) return `url:${source.url}`;
  if ('docId' in source) return `docId:${source.docId}`;
  if ('file' in source)
    return `file:${source.file.name}:${source.file.size}:${source.file.lastModified}`;
  return `bytes:${bytesId(source.bytes)}`;
}

// Silence unused-var: ViewportLike is imported via types.ts for consumers.
export type { ViewportLike };
